// src/components/complaint/LodgeComplaint.tsx
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next'; 
import {
  AlertTriangle, ChevronDown, Upload, X, CheckCircle, Loader2,
  AlertCircle, FileText, Save, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { lodgeComplaint } from '../../../services/complaint.service';
import { getPublicVehicles } from '../../../services/publicVehicle.service';
import { Seo } from '../../components/Seo';

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function LodgeComplaintSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Skeleton className="w-10 h-10 rounded-xl" />
        <div>
          <Skeleton className="h-8 w-52 mb-2" />
          <Skeleton className="h-4 w-56" />
        </div>
      </div>
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <Skeleton className="h-6 w-44 mb-5" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><Skeleton className="h-4 w-36 mb-2" /><Skeleton className="h-12 w-full rounded-xl" /></div>
            <div><Skeleton className="h-4 w-36 mb-2" /><Skeleton className="h-12 w-full rounded-xl" /></div>
            <div className="md:col-span-2"><Skeleton className="h-4 w-28 mb-2" /><Skeleton className="h-36 w-full rounded-xl" /></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <Skeleton className="h-6 w-44 mb-5" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><Skeleton className="h-4 w-32 mb-2" /><Skeleton className="h-12 w-full rounded-xl" /></div>
            <div><Skeleton className="h-4 w-32 mb-2" /><Skeleton className="h-12 w-full rounded-xl" /></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <Skeleton className="h-6 w-36 mb-2" /><Skeleton className="h-4 w-64 mb-5" />
          <Skeleton className="h-40 w-full rounded-xl mb-5" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[1,2,3].map(i => (
              <div key={i} className="border border-[#E5E7EB] rounded-xl p-3 flex gap-3 items-center">
                <Skeleton className="w-10 h-10 rounded-lg" />
                <div className="flex-1"><Skeleton className="h-4 w-20 mb-2" /><Skeleton className="h-3 w-12" /></div>
                <Skeleton className="w-5 h-5 rounded-full" />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <Skeleton className="h-6 w-52 mb-5" />
          <div className="flex flex-wrap gap-3">{[1,2,3].map(i => <Skeleton key={i} className="h-11 w-32 rounded-xl" />)}</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Skeleton className="h-12 sm:w-44 rounded-xl" />
          <Skeleton className="h-12 flex-1 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

type FileEntry = { file: File; id: string; preview?: string };
type FormState = {
  category: string;
  subject: string;
  description: string;
  vehicleId: string;
  sellerId: string;
  sellerName: string;
  contactMethod: string;
};
type FormErrors = Partial<Record<keyof FormState, string>> & {
  attachments?: string;
  submit?: string;
};

export function LodgeComplaint() {
  const { t } = useTranslation('auth'); 
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preVehicle = searchParams.get('vehicle') || '';
  const preSeller = searchParams.get('seller') || '';
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [vehicles, setVehicles] = useState<any[]>([]);

  // Categories with translations
const CATEGORIES = [
  {
    value: 'fraudulent_listing',
    label: t('categories.fraudulent_listing'),
  },
  {
    value: 'incorrect_info',
    label: t('categories.incorrect_info'),
  },
  {
    value: 'fake_seller',
    label: t('categories.fake_seller'),
  },
  {
    value: 'payment_issues',
    label: t('categories.payment_issues'),
  },
  {
    value: 'harassment',
    label: t('categories.harassment'),
  },
  {
    value: 'spam',
    label: t('categories.spam'),
  },
  {
    value: 'not_as_described',
    label: t('categories.not_as_described'),
  },
  {
    value: 'other_disputes',
    label: t('categories.other_disputes'),
  },
];

  const CONTACT_METHODS = [
    t('lodge.contact_methods.email'),
    t('lodge.contact_methods.phone'),
    t('lodge.contact_methods.in_app_message')
  ];

  const [form, setForm] = useState<FormState>({
    category: '',
    subject: '',
    description: '',
    vehicleId: preVehicle,
    sellerId: '',
    sellerName: preSeller,
    contactMethod: CONTACT_METHODS[0],
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [files, setFiles] = useState<FileEntry[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await getPublicVehicles({ page: 1, pageSize: 100 });
        if (res.success) setVehicles(res.Data);
      } catch (err) { /* ignore */ }
    };
    fetchVehicles();
  }, []);

  const set = (k: keyof FormState, v: string) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: undefined }));
  };

  const validate = (): boolean => {
    const errs: FormErrors = {};
    if (!form.category) errs.category = t('lodge.category_error');
    if (!form.subject.trim()) errs.subject = t('lodge.subject_error');
    if (form.description.trim().length < 20) errs.description = t('lodge.description_error');
    if (files.length > 5) errs.attachments = t('lodge.upload_error');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024;
    const valid: FileEntry[] = [];
    let err = '';
    Array.from(newFiles).forEach(f => {
      if (!allowed.includes(f.type)) { err = t('lodge.upload_file_error'); return; }
      if (f.size > maxSize) { err = t('lodge.upload_size_error'); return; }
      if (files.length + valid.length >= 5) { err = t('lodge.upload_error'); return; }
      const id = Math.random().toString(36).slice(2);
      const preview = f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined;
      valid.push({ file: f, id, preview });
    });
    if (err) setErrors(e => ({ ...e, attachments: err }));
    else setErrors(e => ({ ...e, attachments: undefined }));
    setFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (id: string) => setFiles(prev => prev.filter(f => f.id !== id));

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  // First validate frontend fields
  const isValid = validate();

  if (!isValid) {
    // Make absolutely sure button returns to normal state
    setSubmitting(false);
    return;
  }

  // Clear previous submit/API error
  setErrors(prev => ({
    ...prev,
    submit: undefined,
  }));

  setSubmitting(true);

  const formData = new FormData();

  formData.append('category', form.category);
  formData.append('subject', form.subject.trim());
  formData.append('description', form.description.trim());

  if (form.vehicleId) {
    formData.append('vehicleId', form.vehicleId);
  }

  if (form.sellerName) {
    formData.append('sellerName', form.sellerName.trim());
  }

  formData.append('contactMethod', form.contactMethod);

  files.forEach(f => {
    formData.append('evidence', f.file);
  });

  try {
    const res = await lodgeComplaint(formData);

    if (res.success) {
      setReferenceNumber(res.data.referenceNumber);
      setSubmitSuccess(true);

      toast.success(t('messages.submit_success'));
    } else {
      const message =
        res.message ||
        t('messages.submit_failed');

      setErrors(prev => ({
        ...prev,
        submit: message,
      }));
    }

  } catch (err: any) {
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      t('messages.submit_failed');

    setErrors(prev => ({
      ...prev,
      submit: message,
    }));

  } finally {
    // IMPORTANT: Always stop loading
    setSubmitting(false);
  }
};

  if (loading) return <LodgeComplaintSkeleton />;

  if (submitSuccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-10 max-w-md w-full text-center shadow-xl">
          <div className="w-16 h-16 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-[#22C55E]" />
          </div>
          <h2 className="text-2xl text-[#2D323A] mb-2">{t('lodge.success_title')}</h2>
          <p className="text-[#717182] mb-1">{t('lodge.success_message')}</p>
          <p className="text-[#2D323A] mb-6">{t('lodge.success_reference')}: <span className="text-[#1E6DFF]">{referenceNumber}</span></p>
          <p className="text-sm text-[#717182] mb-8">{t('lodge.success_review')} {form.contactMethod}.</p>
          <div className="flex flex-col gap-3">
            <Link to="/complaints" className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors text-center">
              {t('lodge.view_complaints')}
            </Link>
            <Link to="/" className="w-full py-3 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:border-[#1E6DFF] transition-colors text-center">
              {t('lodge.back_home')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <Seo
  title={t('lodge.seo.title')}
  description={t('lodge.seo.description')}
/>
    
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182] transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl text-[#2D323A]">{t('lodge.title')}</h1>
          <p className="text-[#717182] text-sm">{t('lodge.subtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Complaint Details */}
        {errors.submit && (
    <div className="flex items-start gap-2 p-3 rounded-xl bg-[#EF4444]/5 border border-[#EF4444]/20">
      <AlertCircle className="w-4 h-4 text-[#EF4444] mt-0.5 flex-shrink-0" />

      <p className="text-sm text-[#EF4444]">
        {errors.submit}
      </p>
    </div>
  )}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h2 className="text-[#2D323A] mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#F59E0B]" /> {t('lodge.complaint_details')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm text-[#2D323A] mb-1.5">
                {t('lodge.category')} <span className="text-[#EF4444]">*</span>
              </label>
              <div className="relative">
                <select value={form.category} onChange={e => set('category', e.target.value)} className={`w-full appearance-none px-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors text-[#2D323A] ${errors.category ? 'border-[#EF4444]' : 'border-[#E5E7EB]'}`}>
                  <option value="">{t('lodge.category_placeholder')}</option>
                  {CATEGORIES.map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#717182] pointer-events-none" />
              </div>
              {errors.category && <p className="flex items-center gap-1 text-[#EF4444] text-xs mt-1"><AlertCircle className="w-3 h-3" /> {errors.category}</p>}
            </div>
            <div>
              <label className="block text-sm text-[#2D323A] mb-1.5">
                {t('lodge.subject')} <span className="text-[#EF4444]">*</span>
              </label>
              <input
                type="text"
                value={form.subject}
                onChange={e => set('subject', e.target.value)}
                placeholder={t('lodge.subject_placeholder')}
                className={`w-full px-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors ${errors.subject ? 'border-[#EF4444]' : 'border-[#E5E7EB]'}`}
              />
              {errors.subject && <p className="flex items-center gap-1 text-[#EF4444] text-xs mt-1"><AlertCircle className="w-3 h-3" /> {errors.subject}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-[#2D323A] mb-1.5">
                {t('lodge.description')} <span className="text-[#EF4444]">*</span>
                <span className="text-[#717182] ml-2">({form.description.trim().length}/20 {t('lodge.description_min')})</span>
              </label>
              <textarea
                value={form.description}
                onChange={e => set('description', e.target.value)}
                rows={5}
                placeholder={t('lodge.description_placeholder')}
                className={`w-full px-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors resize-none ${errors.description ? 'border-[#EF4444]' : 'border-[#E5E7EB]'}`}
              />
              {errors.description && <p className="flex items-center gap-1 text-[#EF4444] text-xs mt-1"><AlertCircle className="w-3 h-3" /> {errors.description}</p>}
            </div>
          </div>
        </div>

        {/* Related Listing & Seller */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h2 className="text-[#2D323A] mb-4">{t('lodge.related_listing')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm text-[#2D323A] mb-1.5">{t('lodge.related_vehicle')}</label>
              <select
                value={form.vehicleId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  set('vehicleId', selectedId);
                  const selectedVehicle = vehicles.find(v => v.ID === selectedId);
                  if (selectedVehicle) {
                    setForm(prev => ({
                      ...prev,
                      vehicleId: selectedId,
                      sellerId: selectedVehicle.SellerID || '',
                      sellerName: selectedVehicle.SellerName || '',
                    }));
                  } else {
                    setForm(prev => ({
                      ...prev,
                      vehicleId: '',
                      sellerId: '',
                      sellerName: '',
                    }));
                  }
                }}
                className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors"
              >
                <option value="">{t('lodge.related_vehicle_placeholder')}</option>
                {vehicles.map(v => (
                  <option key={v.ID} value={v.ID}>{v.Title || `${v.MakeName} ${v.ModelName} ${v.YearOfRegistration}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#2D323A] mb-1.5">{t('lodge.related_seller')}</label>
              <input
                type="text"
                value={form.sellerName}
                onChange={e => set('sellerName', e.target.value)}
                placeholder={t('lodge.related_seller_placeholder')}
                className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Upload Evidence */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h2 className="text-[#2D323A] mb-1">{t('lodge.upload_evidence')}</h2>
          <p className="text-sm text-[#717182] mb-4">{t('lodge.upload_hint')}</p>
          <div
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${errors.attachments ? 'border-[#EF4444] bg-[#EF4444]/5' : 'border-[#E5E7EB] hover:border-[#1E6DFF] hover:bg-[#1E6DFF]/5'}`}
          >
            <Upload className="w-8 h-8 text-[#717182] mx-auto mb-2" />
            <p className="text-sm text-[#2D323A]">{t('lodge.upload_click')}</p>
            <p className="text-xs text-[#717182] mt-1">{files.length}/5 {t('lodge.upload_count')}</p>
            <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={e => handleFiles(e.target.files)} />
          </div>
          {errors.attachments && <p className="flex items-center gap-1 text-[#EF4444] text-xs mt-2"><AlertCircle className="w-3 h-3" /> {errors.attachments}</p>}

          {files.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {files.map(f => (
                <div key={f.id} className="relative group bg-[#F5F7FA] rounded-xl p-3 flex items-center gap-2 border border-[#E5E7EB]">
                  {f.preview ? (
                    <img src={f.preview} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 bg-[#1E6DFF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-[#1E6DFF]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#2D323A] truncate">{f.file.name}</p>
                    <p className="text-xs text-[#717182]">{(f.file.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <button type="button" onClick={() => removeFile(f.id)} className="text-[#717182] hover:text-[#EF4444] transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Method */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h2 className="text-[#2D323A] mb-4">{t('lodge.contact_method')}</h2>
          <div className="flex flex-wrap gap-3">
            {CONTACT_METHODS.map(m => (
              <button
                key={m}
                type="button"
                onClick={() => set('contactMethod', m)}
                className={`px-4 py-2.5 rounded-xl border text-sm transition-colors ${form.contactMethod === m ? 'bg-[#1E6DFF] text-white border-[#1E6DFF]' : 'bg-[#F5F7FA] text-[#2D323A] border-[#E5E7EB] hover:border-[#1E6DFF]'}`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => toast.info(t('messages.draft_saved'))}
            className="flex-1 sm:flex-none px-6 py-3 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:border-[#1E6DFF] transition-colors flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" /> {t('lodge.save_draft')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> {t('lodge.submitting')}</>
            ) : (
              <><AlertTriangle className="w-4 h-4" /> {t('lodge.submit')}</>
            )}
          </button>
        </div>
      </form>
    </div>
    </>
  );
}